import React, {useEffect, useState, useMemo, useRef} from 'react';
import {Image as RNImage} from 'react-native';
// Use the same @expo/vector-icons IconSet that the rest of the app uses.
// This guarantees the MCI font is loaded by RN-Web on first render.
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import CCColors from '@/Utils/CCColors';
// Brand assets — imported the same way as existing project SVGs (see
// src/Screens/Classroom/components/Leaderboard/Leaderboard.tsx). Webpack
// resolves these to served URLs.
import CrownAsset from '@/Assets/Recognition/Crown.svg';
import TrophyAsset from '@/Assets/Recognition/Trophy.svg';
import FlashAsset from '@/Assets/Recognition/Flash.svg';
import StreakAsset from '@/Assets/Recognition/Streak.svg';
import AccuracyAsset from '@/Assets/Recognition/Accuracy.svg';
import StarAsset from '@/Assets/Recognition/Star.svg';
import OnTimeAsset from '@/Assets/Recognition/OnTime.png';
import GoodThinkingAsset from '@/Assets/Recognition/GoodThinking.png';
import GreatEffortAsset from '@/Assets/Recognition/GreatEffort.png';

// Brand assets keyed by short name. Values are the imported asset
// sources — webpack converts SVG/PNG imports to URLs that RNImage can use.
const BRAND_ASSETS: Record<string, any> = {
  crown:        CrownAsset,
  trophy:       TrophyAsset,
  flash:        FlashAsset,
  streak:       StreakAsset,
  accuracy:     AccuracyAsset,
  star:         StarAsset,
  ontime:       OnTimeAsset,
  goodthinking: GoodThinkingAsset,
  greateffort:  GreatEffortAsset,
};

interface McIconProps {name: string; size: number; color: string; style?: any;}
// Wraps the project's existing MaterialCommunityIcons component so the font
// loads exactly like everywhere else in the app.
function McIcon({name, size, color, style}: McIconProps) {
  return (
    <MaterialCommunityIcons name={name as any} size={size} color={color} style={style} />
  );
}

interface RecIconProps {name: string; size: number; color: string; style?: any;}
// Unified icon renderer: brand <RNImage> if available, else MCI font glyph.
function RecIcon({name, size, color, style}: RecIconProps) {
  const asset = BRAND_ASSETS[name];
  if (asset) {
    return (
      <RNImage
        source={asset}
        resizeMode="contain"
        style={[{width: size, height: size}, style]}
      />
    );
  }
  return <McIcon name={name} size={size} color={color} style={style} />;
}

function isBrandIcon(name: string) {
  return !!BRAND_ASSETS[name];
}

// Resolve a brand asset to a string URL (for raw DOM <img>, e.g. StarFlyHtml).
// On RN-Web webpack imports already return URL strings; on native they
// return source objects with .uri.
function brandUrl(name: string): string {
  const asset = BRAND_ASSETS[name];
  if (!asset) return '';
  if (typeof asset === 'string') return asset;
  if (asset.uri) return asset.uri;
  if (asset.default) return asset.default;
  return '';
}

// ─────────────────────────────────────────────────────────────────────────────
// Classroom Recognition overlays — Claymorphism (Mobile) style on CCColors palette.
//
// Style: chunky, soft 3D, toy-like surfaces. Border-radius 20–32px.
// Multi-layer shadow (inner highlight + outer accent drop). Spring overshoot
// on entrance. ONE idle micro-animation per overlay (Disney "appeal").
//
// Psychology contract (see docs/superpowers/research/2026-05-20-classroom-recognition-psychology.md):
//   - Process praise, not person praise. No "Genius/Master/Smartest" wording.
//   - Variable rewards time-bounded + skill-coupled (Mystery 10s, no streak guilt).
//   - Streaks private (only the student sees their own).
//   - Public moments name behaviors, never compare students.
//   - Respect prefers-reduced-motion.
// ─────────────────────────────────────────────────────────────────────────────

// Palette (CCColors brand only — no invented hex)
const C = {
  brown:       CCColors.Primary.Brown,            // #4D3F37
  brownLight:  CCColors.TextColor.LightGrey,      // #4D3F37CC
  aqua:        CCColors.BlueAqua,                 // #3DAB9E
  aquaLight:   CCColors.GreenLight,               // #21CDBD29
  orange:      CCColors.Orange,                   // #E17B21
  orangeLight: CCColors.RedLight,                 // #FF854B29
  purple:      CCColors.Purple,                   // #937ADB
  purpleLight: CCColors.MeetingStatus.Purple,     // #937ADB26
  purpleDark:  '#5C46A8',                          // deeper purple for Legendary bg
  gold:        CCColors.Primary.Default,          // #FACF47
  goldDeep:    '#D1AB41',
  goldLight:   CCColors.Primary.lightYellow,      // #FEF2B5
  cream:       CCColors.Primary.lightBrown,       // #FAF6EB
  brightYellow:CCColors.BrightYellow,             // #F9E9B8
  textMuted:   CCColors.Grey.Grey,                // #7D7D7D
  white:       CCColors.White,                    // #FFFFFF
};

const FONT_DISPLAY = '"Baloo 2", "Inter", -apple-system, BlinkMacSystemFont, sans-serif';
const FONT_BODY = '"Inter", "DM Sans", -apple-system, BlinkMacSystemFont, sans-serif';

// AWARDS_MAP — every label is process or action, never trait. Coach-attributed subtitle.
// `icon` is a MaterialCommunityIcons name (see https://oblador.github.io/react-native-vector-icons/).
const AWARDS_MAP: Record<string, {label: string; icon: string; color: string; tint: string; subtitle?: string; badgeLabel?: string}> = {
  good_try:         {label: 'Good Try',         icon: 'sprout',            color: C.aqua,   tint: C.aquaLight},
  great_effort:     {label: 'Great Effort',     icon: 'greateffort',       color: C.orange, tint: C.orangeLight},
  good_thinking:    {label: 'Good Thinking',    icon: 'goodthinking',      color: C.gold,   tint: C.goldLight},
  focused:          {label: 'Focused',          icon: 'accuracy',          color: C.purple, tint: C.purpleLight},
  team_player:      {label: 'Team Player',      icon: 'handshake',         color: C.brown,  tint: '#4D3F3712'},
  joined_on_time:   {label: 'On Time',          icon: 'ontime',            color: C.aqua,   tint: C.aquaLight, badgeLabel: 'Class started', subtitle: '+1 star for being on time'},
  homework_complete:{label: 'Homework Done',    icon: 'book-open-variant', color: C.aqua,   tint: C.aquaLight, badgeLabel: 'Homework streak', subtitle: '+2 stars from coach'},
  correct_solve:    {label: 'Solved',           icon: 'check-bold',        color: C.aqua,   tint: C.aquaLight, subtitle: '+1 star correct solve'},
  puzzle_attempt:   {label: 'Nice Try',         icon: 'chess-pawn',        color: C.brown,  tint: '#4D3F3712', subtitle: '+1 star for trying'},
};

const STREAK_TIERS = [
  {threshold: 7, name: 'LEGENDARY',   color: C.purple, icon: 'crown'},
  {threshold: 5, name: 'Unstoppable', color: C.orange, icon: 'flash'},
  {threshold: 3, name: 'On a Roll',   color: C.aqua,   icon: 'streak'},
];

function tierFor(streak: number) {
  return STREAK_TIERS.find(t => streak >= t.threshold) ?? null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Clay shadow helper. Returns multi-layer shadow string per accent.
// Inner highlight (top, white) + inner darken (bottom) + outer drop (accent-tinted).
// ─────────────────────────────────────────────────────────────────────────────
function clayShadow(accent: string, depth: 'sm' | 'md' | 'lg' = 'md') {
  const dropSize = depth === 'sm' ? '12px' : depth === 'lg' ? '36px' : '24px';
  const dropY = depth === 'sm' ? '4px' : depth === 'lg' ? '14px' : '8px';
  return `inset 0 2px 4px rgba(255,255,255,0.85), inset 0 -2px 4px rgba(0,0,0,0.06), 0 ${dropY} ${dropSize} ${accent}33`;
}

// Bubble shadow for chunky icon containers (more pronounced 3D)
function bubbleShadow(accent: string) {
  return `inset 0 3px 6px rgba(255,255,255,0.5), inset 0 -3px 6px rgba(0,0,0,0.12), 0 6px 14px ${accent}66, 0 0 0 2px rgba(255,255,255,0.4)`;
}

// All brand icons served from web/public/recognition/ via <img>.
// URL form for raw DOM use (StarFlyHtml animates <img> directly).
const STAR_IMG_URL = brandUrl('star');

// ─────────────────────────────────────────────────────────────────────────────
// Font + keyframes injection. Runs once at module load.
// Includes prefers-reduced-motion strip — disables all loops on user preference.
// ─────────────────────────────────────────────────────────────────────────────
const KEYFRAMES_ID = 'cc-rec-keyframes';
const KEYFRAMES_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;700;800&display=swap');

@keyframes rec-fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes rec-pop-overshoot {
  from { opacity: 0; transform: scale(0.85); }
  to   { opacity: 1; transform: scale(1); }
}
@keyframes rec-slide-down {
  from { opacity: 0; transform: translate3d(0, -48px, 0); }
  to   { opacity: 1; transform: translate3d(0, 0, 0); }
}
@keyframes rec-wobble {
  0%, 100% { transform: rotate(-4deg); }
  50%      { transform: rotate(4deg); }
}
@keyframes rec-bounce-soft {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-6px); }
}
@keyframes rec-sparkle-soft {
  0%, 100% { opacity: 0; transform: scale(0.6); }
  50%      { opacity: 0.9; transform: scale(1); }
}
@keyframes rec-blob-drift {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33%      { transform: translate(20px, -10px) scale(1.05); }
  66%      { transform: translate(-15px, 12px) scale(0.95); }
}
@keyframes rec-confetti-fall-once {
  0%   { opacity: 0; transform: translateY(-40px) rotate(0deg); }
  10%  { opacity: 1; }
  90%  { opacity: 1; }
  100% { opacity: 0; transform: translateY(620px) rotate(720deg); }
}
@keyframes rec-particle-burst-once {
  0%   { opacity: 0; transform: translate(0, 0) scale(0.5); }
  30%  { opacity: 1; }
  100% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(0.7); }
}
@keyframes rec-progress-ring-pulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.85; }
}

/* Reduced-motion: respect user preference. Strip loops, keep fade only. */
@media (prefers-reduced-motion: reduce) {
  .cc-rec-anim,
  .cc-rec-anim * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
`;

function injectKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(KEYFRAMES_ID)) return;
  const s = document.createElement('style');
  s.id = KEYFRAMES_ID;
  s.textContent = KEYFRAMES_CSS;
  document.head.appendChild(s);
}

injectKeyframes();

// ─────────────────────────────────────────────────────────────────────────────
// FloatingBlobs — slow-drifting decorative blobs for fullscreen overlays.
// Two large soft blobs, opposite corners. Drift loop ~6s. Disabled on reduced-motion.
// ─────────────────────────────────────────────────────────────────────────────
function FloatingBlobs({colors}: {colors: [string, string]}) {
  return (
    <div style={{position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none'}}>
      <div style={{
        position: 'absolute', top: '-15%', left: '-10%',
        width: 360, height: 360, borderRadius: '50%',
        background: `radial-gradient(circle, ${colors[0]}77 0%, transparent 70%)`,
        willChange: 'transform',
        animation: 'rec-blob-drift 8s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute', bottom: '-15%', right: '-10%',
        width: 420, height: 420, borderRadius: '50%',
        background: `radial-gradient(circle, ${colors[1]}77 0%, transparent 70%)`,
        willChange: 'transform',
        animation: 'rec-blob-drift 10s ease-in-out infinite reverse',
      }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. PublicAwardToastHtml — top-center clay pill. Coach-attributed.
//    Single sparkle (Disney appeal). No icon bounce loop.
//    Copy: "{Name} got {Award}" (process praise — award is behavior-named).
// ─────────────────────────────────────────────────────────────────────────────
const AWARD_BADGE: Record<string, {label: string; icon: string; color: string}> = {
  correct_solve:        {label: 'Correct',      icon: '✓',  color: '#22A06B'},
  fastest_finger:       {label: 'Speed Flash',  icon: '⚡', color: '#FF7A1A'},
  great_effort:         {label: 'Great Effort', icon: '💪', color: '#E17B21'},
  good_try:             {label: 'Good Try',     icon: '🌱', color: '#3DAB9E'},
  good_thinking:        {label: 'Good Thinking',icon: '💡', color: '#FACF47'},
  focused:              {label: 'Focused',      icon: '🎯', color: '#937ADB'},
  team_player:          {label: 'Team Player',  icon: '🤝', color: '#4D3F37'},
  tournament_game_win:  {label: 'Game Won!',    icon: '🏆', color: '#FACF47'},
  joined_on_time:       {label: 'On Time',      icon: '⏰', color: '#3DAB9E'},
  homework_complete:    {label: 'Homework',     icon: '📚', color: '#3DAB9E'},
};

interface PublicAwardToastHtmlProps {studentName: string; awardKey: string; onDone: () => void; duration?: number; awards?: string[];}

export function PublicAwardToastHtml({studentName, awardKey, onDone, duration = 2800, awards}: PublicAwardToastHtmlProps) {
  const a = AWARDS_MAP[awardKey] ?? AWARDS_MAP.good_try;
  const multiAwards = awards && awards.length > 1 ? awards : null;
  useEffect(() => {
    const t = setTimeout(() => onDone(), duration);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="cc-rec-anim" style={{
      position: 'absolute', top: 22, left: '50%', transform: 'translateX(-50%)',
      pointerEvents: 'none',
      animation: 'rec-pop-overshoot 360ms cubic-bezier(.34,1.56,.64,1)',
      fontFamily: FONT_BODY,
    }}>
      <div style={{
        position: 'relative',
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '14px 22px 14px 16px',
        background: C.white,
        border: `3px solid ${a.color}`,
        borderRadius: 28,
        boxShadow: clayShadow(a.color, 'md'),
        minWidth: 300, maxWidth: 440,
      }}>
        {/* single Disney "appeal" sparkle */}
        <div style={{
          position: 'absolute', top: -10, right: 20, fontSize: 16,
          animation: 'rec-sparkle-soft 1.8s ease-in-out infinite',
        }}>✨</div>
        {/* icon — brand SVG floats free; MCI sits in chunky clay bubble */}
        {isBrandIcon(a.icon) ? (
          <div style={{
            width: 56, height: 56,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            filter: `drop-shadow(0 0 8px ${a.color}55) drop-shadow(0 4px 8px rgba(0,0,0,0.18))`,
          }}><RecIcon name={a.icon} size={52} color={a.color} /></div>
        ) : (
          <div style={{
            width: 52, height: 52, borderRadius: 26,
            background: `linear-gradient(135deg, ${a.color} 0%, ${a.color}DD 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            boxShadow: bubbleShadow(a.color),
          }}><RecIcon name={a.icon} size={26} color={C.white} /></div>
        )}
        <div style={{flex: 1, minWidth: 0}}>
          <div style={{
            fontFamily: FONT_DISPLAY,
            fontSize: 11, fontWeight: 800, color: a.color,
            textTransform: 'uppercase', letterSpacing: 1.5, lineHeight: 1,
          }}>
            {a.badgeLabel ?? 'New badge'}
          </div>
          <div style={{
            fontFamily: FONT_DISPLAY,
            fontSize: 17, fontWeight: 800, color: C.brown,
            lineHeight: 1.25, marginTop: 5,
          }}>
            {studentName} got <span style={{color: a.color}}>{a.label}</span>
          </div>
          <div style={{fontSize: 12, fontWeight: 600, color: C.brownLight, marginTop: 3}}>
            {a.subtitle ?? '+1 star from coach'}
          </div>
          {multiAwards && (
            <div style={{display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap'}}>
              {multiAwards.map((key) => {
                const m = AWARD_BADGE[key] ?? {label: key, icon: '⭐', color: '#5B6BFA'};
                return (
                  <span key={key} style={{
                    background: m.color + '22',
                    border: `1px solid ${m.color}44`,
                    borderRadius: 12,
                    padding: '2px 8px',
                    fontSize: 12,
                    fontWeight: 700,
                    color: m.color,
                  }}>{m.icon} {m.label}</span>
                );
              })}
            </div>
          )}
        </div>
        <button
          onClick={onDone}
          style={{
            position: 'absolute', top: 8, right: 10,
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 18, color: C.brownLight, lineHeight: 1, padding: 4,
          }}
          aria-label="Dismiss">×</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. SpeedFlashHtml — clay center pill. Orange accent (high-arousal warm).
//    Copy: "Fastest solve · {Name} · {time}" (per-puzzle metric, no class ranking).
// ─────────────────────────────────────────────────────────────────────────────
interface SpeedFlashHtmlProps {name: string; solveTime: string; onDone: () => void;}

export function SpeedFlashHtml({name, solveTime, onDone}: SpeedFlashHtmlProps) {
  useEffect(() => {
    const t = setTimeout(() => onDone(), 2800);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="cc-rec-anim" style={{
      position: 'fixed',
      top: 24,
      left: '50%',
      transform: 'translateX(-50%)',
      pointerEvents: 'none',
      fontFamily: FONT_BODY,
      zIndex: 2147483647,
    }}>
      <div style={{
        position: 'relative',
        display: 'flex', alignItems: 'center', gap: 18,
        padding: '18px 28px 18px 22px',
        background: `linear-gradient(135deg, ${C.cream} 0%, ${C.white} 100%)`,
        border: `3px solid ${C.orange}`,
        borderRadius: 32,
        boxShadow: clayShadow(C.orange, 'lg'),
        minWidth: 340,
        animation: 'rec-slide-down 360ms cubic-bezier(.34,1.56,.64,1)',
      }}>
        {/* one sparkle for appeal */}
        <div style={{
          position: 'absolute', top: -14, right: 28, fontSize: 18,
          animation: 'rec-sparkle-soft 1.6s ease-in-out infinite',
        }}>✨</div>
        {/* big chunky rocket bubble */}
        <div style={{
          width: 64, height: 64, borderRadius: 32,
          background: `linear-gradient(135deg, ${C.orange} 0%, ${C.goldDeep} 100%)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          boxShadow: bubbleShadow(C.orange),
        }}><McIcon name="rocket-launch" size={32} color={C.white} /></div>
        <div style={{flex: 1, minWidth: 0}}>
          <div style={{
            fontFamily: FONT_DISPLAY,
            fontSize: 11, fontWeight: 800, color: C.orange,
            letterSpacing: 2.2, textTransform: 'uppercase', lineHeight: 1,
          }}>Fastest solve</div>
          <div style={{
            fontFamily: FONT_DISPLAY,
            fontSize: 22, fontWeight: 800, color: C.brown,
            marginTop: 5, lineHeight: 1.15,
          }}>{name}</div>
          {solveTime ? (
            <div style={{
              fontSize: 13, fontWeight: 700, color: C.brownLight,
              marginTop: 2,
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <McIcon name="flag-checkered" size={13} color={C.brownLight} />
              <span>{solveTime}</span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. MysteryBonusHtml — clay top-right card. Non-blocking (pointerEvents none on
//    wrapper outside). 10s countdown ring. Single wobble loop on gift bubble.
//    Copy: "Mystery Time · Next solve = 3× stars · Solve quick"
//    Variable reward is bounded (10s) + skill-coupled (must still solve).
// ─────────────────────────────────────────────────────────────────────────────
interface MysteryBonusHtmlProps {onDone: () => void;}

export function MysteryBonusHtml({onDone}: MysteryBonusHtmlProps) {
  const [secs, setSecs] = useState(10);
  useEffect(() => {
    if (secs <= 0) { onDone(); return; }
    const t = setTimeout(() => setSecs(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secs]);
  const r = 22;
  const circ = 2 * Math.PI * r;
  return (
    <div className="cc-rec-anim" style={{
      position: 'absolute',
      top: 90, right: 22,
      width: 340,
      pointerEvents: 'none',
      fontFamily: FONT_BODY,
    }}>
      <div style={{
        position: 'relative',
        background: `linear-gradient(135deg, ${C.white} 0%, ${C.purpleLight} 100%)`,
        border: `3px solid ${C.purple}`,
        borderRadius: 26,
        padding: '16px 18px',
        boxShadow: clayShadow(C.purple, 'lg'),
        animation: 'rec-pop-overshoot 360ms cubic-bezier(.34,1.56,.64,1)',
      }}>
        <div style={{display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10}}>
          {/* chunky gift bubble — only looping element */}
          <div style={{
            width: 48, height: 48, borderRadius: 24,
            background: `linear-gradient(135deg, ${C.purple} 0%, ${C.purpleDark} 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: bubbleShadow(C.purple),
            willChange: 'transform',
            animation: 'rec-wobble 1.6s ease-in-out infinite',
          }}><McIcon name="gift" size={26} color={C.white} /></div>
          <div style={{flex: 1}}>
            <div style={{
              fontFamily: FONT_DISPLAY,
              fontSize: 11, fontWeight: 800, color: C.purple,
              letterSpacing: 1.6, textTransform: 'uppercase', lineHeight: 1,
            }}>Mystery time</div>
            <div style={{
              fontFamily: FONT_DISPLAY,
              fontSize: 16, fontWeight: 800, color: C.brown,
              marginTop: 4, lineHeight: 1.2,
            }}>Next solve = 3× stars</div>
          </div>
        </div>
        <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
          <div style={{position: 'relative', width: 52, height: 52, flexShrink: 0}}>
            <svg width="52" height="52" style={{display: 'block'}}>
              <circle cx="26" cy="26" r={r} fill={`${C.purple}11`} stroke={`${C.purple}44`} strokeWidth="3" />
              <circle
                cx="26" cy="26" r={r} fill="none"
                stroke={C.purple} strokeWidth="4"
                strokeDasharray={circ}
                strokeDashoffset={circ * (1 - secs / 10)}
                transform="rotate(-90 26 26)"
                style={{transition: 'stroke-dashoffset 1s linear'}}
                strokeLinecap="round"
              />
            </svg>
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: FONT_DISPLAY,
              fontSize: 16, fontWeight: 800, color: C.purpleDark,
            }}>{secs}</div>
          </div>
          <div style={{
            fontSize: 13, fontWeight: 600, color: C.brownLight,
            flex: 1, lineHeight: 1.3,
          }}>Solve quick to win big</div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. StreakFlashHtml — lower tiers = clay top pill; Legendary = clay fullscreen.
//    Per psych contract: streak overlays are PRIVATE to the student (current
//    classroom code shows only the local student's streak — preserved).
//    Process copy: "On a Roll" "Unstoppable" "Legendary" — describe continuity,
//    not innate ability.
// ─────────────────────────────────────────────────────────────────────────────
interface StreakFlashHtmlProps {streak: number; onDone: () => void;}

function LegendaryParticles({colors}: {colors: string[]}) {
  const particles = useMemo(() => Array.from({length: 8}).map((_, i) => {
    const angle = (Math.PI * 2 * i) / 8 + (Math.random() - 0.5) * 0.3;
    const dist = 200 + Math.random() * 120;
    return {
      dx: Math.cos(angle) * dist,
      dy: Math.sin(angle) * dist,
      color: colors[i % colors.length],
      size: 10 + Math.random() * 8,
      delay: Math.random() * 0.1,
      duration: 0.8,
    };
  }), []);
  return (
    <div style={{position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none'}}>
      {particles.map((p, i) => (
        <span key={i} style={{
          position: 'absolute',
          left: 0, top: 0,
          width: p.size, height: p.size,
          borderRadius: '50%',
          background: p.color,
          boxShadow: `0 0 ${p.size}px ${p.color}88`,
          // @ts-ignore custom prop
          '--dx': `${p.dx}px`,
          '--dy': `${p.dy}px`,
          animation: `rec-particle-burst-once ${p.duration}s cubic-bezier(.16,.84,.34,1) ${p.delay}s forwards`,
        } as any} />
      ))}
    </div>
  );
}

export function StreakFlashHtml({streak, onDone}: StreakFlashHtmlProps) {
  const tier = tierFor(streak);
  useEffect(() => {
    const t = setTimeout(() => onDone(), tier?.name === 'LEGENDARY' ? 3200 : 2200);
    return () => clearTimeout(t);
  }, []);
  if (!tier) return null;

  if (tier.name === 'LEGENDARY') {
    return (
      <div className="cc-rec-anim" style={{
        position: 'absolute', inset: 0,
        background: `radial-gradient(circle at center, ${C.purple} 0%, ${C.purpleDark} 100%)`,
        boxShadow: 'inset 0 0 280px rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'auto',
        fontFamily: FONT_BODY,
        animation: 'rec-fade-in 240ms ease-out',
        overflow: 'hidden',
      }}>
        <FloatingBlobs colors={[C.gold, C.brightYellow]} />
        <LegendaryParticles colors={[C.white, C.gold, C.brightYellow]} />
        <div style={{
          textAlign: 'center',
          position: 'relative',
          zIndex: 2,
          animation: 'rec-pop-overshoot 480ms cubic-bezier(.34,1.56,.64,1)',
        }}>
          {/* big crown — brand asset served from /recognition/Crown.svg */}
          <div style={{
            width: 140, height: 140,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            filter: `drop-shadow(0 0 24px ${C.gold}99) drop-shadow(0 8px 16px rgba(0,0,0,0.3))`,
            marginBottom: 16,
            willChange: 'transform',
            animation: 'rec-bounce-soft 1.6s ease-in-out infinite',
          }}>
            <RNImage source={CrownAsset} resizeMode="contain" style={{width: 140, height: 140}} />
          </div>
          <div style={{
            fontFamily: FONT_DISPLAY,
            fontSize: 14, fontWeight: 800, color: C.gold,
            letterSpacing: 5, lineHeight: 1,
            textShadow: '0 2px 0 rgba(0,0,0,0.22)',
          }}>STREAK</div>
          <div style={{
            fontFamily: FONT_DISPLAY,
            fontSize: 60, fontWeight: 800, color: C.white,
            letterSpacing: 4, lineHeight: 1.05, marginTop: 8,
            textShadow: `0 4px 0 ${C.purpleDark}, 0 10px 30px rgba(0,0,0,0.3)`,
          }}>LEGENDARY</div>
          {/* gold clay pill with streak count */}
          <div style={{
            marginTop: 22, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 26px',
            background: `linear-gradient(135deg, ${C.gold} 0%, ${C.brightYellow} 100%)`,
            borderRadius: 999,
            border: `3px solid ${C.white}`,
            fontFamily: FONT_DISPLAY,
            fontSize: 20, fontWeight: 800, color: C.purpleDark,
            boxShadow: `${clayShadow(C.gold, 'md')}, 0 0 24px ${C.gold}66`,
          }}>
            <McIcon name="fire" size={22} color={C.purpleDark} />
            <span>{streak} in a row</span>
          </div>
        </div>
      </div>
    );
  }

  // Tier 3 / 5 — clay top pill (private to student, top of screen)
  return (
    <div className="cc-rec-anim" style={{
      position: 'absolute', top: 0, left: 0, right: 0,
      display: 'flex', justifyContent: 'center',
      pointerEvents: 'none',
      paddingTop: 64,
      animation: 'rec-pop-overshoot 360ms cubic-bezier(.34,1.56,.64,1)',
      fontFamily: FONT_BODY,
    }}>
      <div style={{
        padding: '14px 24px',
        background: C.white,
        border: `3px solid ${tier.color}`,
        borderRadius: 999,
        boxShadow: clayShadow(tier.color, 'md'),
        display: 'flex', alignItems: 'center', gap: 14,
      }}>
        {/* brand SVG icon (no bubble bg — SVG carries own colors) */}
        <div style={{
          width: 38, height: 38,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          filter: `drop-shadow(0 0 6px ${tier.color}77) drop-shadow(0 3px 6px rgba(0,0,0,0.18))`,
        }}><RecIcon name={tier.icon} size={36} color={C.white} /></div>
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1}}>
          <span style={{
            fontFamily: FONT_DISPLAY,
            fontSize: 10, fontWeight: 800, color: tier.color,
            letterSpacing: 1.8, textTransform: 'uppercase',
          }}>Streak</span>
          <span style={{
            fontFamily: FONT_DISPLAY,
            fontSize: 20, fontWeight: 800, color: C.brown,
            marginTop: 3,
          }}>
            {tier.name === 'On a Roll' ? 'On a Roll' : 'Unstoppable'}
          </span>
        </div>
        <div style={{
          padding: '6px 14px',
          background: `linear-gradient(135deg, ${tier.color} 0%, ${tier.color}CC 100%)`,
          borderRadius: 999,
          fontFamily: FONT_DISPLAY,
          fontSize: 14, fontWeight: 800, color: C.white,
          boxShadow: `inset 0 1px 2px rgba(255,255,255,0.4)`,
        }}>{streak} in a row</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. CelebrationHtml — clay fullscreen. Warm gold→orange→purple gradient.
//    Floating blobs. 8 confetti single-pass (no infinite loop).
//    Cards: thick clay borders, NO comparative rotation.
//    Awards always include Coach's Star fallback so no kid is "last".
// ─────────────────────────────────────────────────────────────────────────────
interface CelebrationAward {award: string; user_id: number; name: string;}
interface CelebrationHtmlProps {
  role: 'coach' | 'student';
  awards: CelebrationAward[];
  allEarned: boolean;
  coachStarStudentName?: string;
  onDone: () => void;
  isPreview?: boolean;
  currentUserName?: string;
}

const CELEBRATION_AWARD_META: Record<string, {title: string; subtitle: string; icon: string; color: string}> = {
  top_solver:           {title: 'Top Solver',     subtitle: 'Most correct puzzles',   icon: 'trophy',           color: C.gold},
  top_performer:        {title: 'Top Performer',  subtitle: 'Most stars earned',       icon: 'star',             color: C.gold},
  most_active:          {title: 'Most Active',    subtitle: 'Most attempts made',      icon: 'rocket-launch',    color: C.aqua},
  great_effort:         {title: 'Great Effort',   subtitle: 'Hardest worker today',    icon: 'arm-flex',         color: C.orange},
  fastest_finger:       {title: 'Fastest Finger', subtitle: 'Quickest to answer',      icon: 'lightning-bolt',   color: '#E05FD0'},
  good_try_leader:      {title: 'Good Try',        subtitle: 'Most good tries',         icon: 'leaf',             color: C.aqua},
  great_effort_leader:  {title: 'Great Effort',   subtitle: 'Most effort shown',       icon: 'arm-flex',         color: C.orange},
  good_thinking_leader: {title: 'Good Thinking',  subtitle: 'Best thinker today',      icon: 'lightbulb',        color: C.gold},
  focused_leader:       {title: 'Most Focused',   subtitle: 'Laser sharp focus',       icon: 'eye',              color: C.purple},
  team_player_leader:   {title: 'Team Player',    subtitle: 'Best team spirit',        icon: 'account-group',    color: '#4D3F37'},
  coach_star_student:   {title: "Coach's Star",   subtitle: 'Picked by your coach',    icon: 'star-four-points', color: C.purple},
  coachs_star:          {title: "Coach's Star",   subtitle: 'Picked by your coach',    icon: 'star-four-points', color: C.purple},
  everyone_earned:      {title: 'Everyone Earned',subtitle: 'Whole class scored',      icon: 'party-popper',     color: C.brown},
};

export function CelebrationHtml({role, awards, allEarned, coachStarStudentName, onDone, isPreview = false, currentUserName}: CelebrationHtmlProps) {
  const confettiEmojis = ['🎉', '⭐', '✨'];
  const cards: Array<{key: string; title: string; subtitle: string; icon: string; color: string; student: string}> = [];

  // render all awards present in the array (coach-finalized list)
  awards.forEach(a => {
    if (a.award === 'everyone_earned') return; // handled separately below
    const meta = CELEBRATION_AWARD_META[a.award];
    if (!meta) return;
    const student = a.award === 'coach_star_student' ? (coachStarStudentName ?? a.name ?? '') : a.name;
    if (!student) return;
    cards.push({key: a.award, ...meta, student});
  });

  if (allEarned) {
    const meta = CELEBRATION_AWARD_META.everyone_earned;
    cards.push({key: 'everyone_earned', ...meta, student: 'Class'});
  }

  return (
    <div className="cc-rec-anim" style={{
      position: 'absolute', inset: 0,
      background: `linear-gradient(135deg, ${C.gold} 0%, ${C.orange} 55%, ${C.purple} 100%)`,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
      padding: '36px 22px 24px',
      pointerEvents: 'auto',
      overflow: 'auto',
      fontFamily: FONT_BODY,
      animation: 'rec-fade-in 320ms ease-out',
    }}>
      <button
        onClick={onDone}
        style={{
          position: 'fixed', top: 14, right: 18,
          zIndex: 10,
          background: 'rgba(255,255,255,0.22)',
          border: '2px solid rgba(255,255,255,0.55)',
          borderRadius: 999,
          cursor: 'pointer',
          width: 36, height: 36,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 20, color: C.white, lineHeight: 1,
          boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
        }}
        aria-label="Close">×</button>
      <FloatingBlobs colors={[C.white, C.gold]} />
      {Array.from({length: 8}).map((_, i) => (
        <div key={i} style={{
          position: 'absolute',
          left: `${(i * 47 + 7) % 100}%`,
          top: '-8%',
          animation: `rec-confetti-fall-once ${2.6 + (i % 3) * 0.4}s ease-in ${(i % 4) * 0.18}s forwards`,
          fontSize: 20 + (i % 3) * 6,
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.18))',
        }}>{confettiEmojis[i % confettiEmojis.length]}</div>
      ))}

      <div style={{
        textAlign: 'center',
        marginBottom: 24,
        position: 'relative',
        zIndex: 2,
        willChange: 'transform',
        animation: 'rec-slide-down 560ms cubic-bezier(.22,.94,.4,1.04) both',
      }}>
        {/* big trophy / popper hero */}
        {role === 'coach' ? (
          <div style={{
            width: 130, height: 145,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            filter: `drop-shadow(0 0 30px ${C.gold}99) drop-shadow(0 10px 18px rgba(0,0,0,0.28))`,
            marginBottom: 12,
            willChange: 'transform',
            animation: 'rec-bounce-soft 1.8s ease-in-out 560ms infinite',
          }}>
            <RNImage source={TrophyAsset} resizeMode="contain" style={{width: 130, height: 145}} />
          </div>
        ) : (
          <div style={{
            width: 96, height: 96, borderRadius: 48,
            background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldDeep} 100%)`,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: bubbleShadow(C.gold) + `, 0 0 40px ${C.gold}99`,
            marginBottom: 12,
            willChange: 'transform',
            animation: 'rec-bounce-soft 1.8s ease-in-out 560ms infinite',
          }}><McIcon name="party-popper" size={52} color={C.white} /></div>
        )}
        <div style={{
          fontFamily: FONT_DISPLAY,
          fontSize: 11, fontWeight: 800, color: C.white,
          letterSpacing: 4, marginBottom: 8,
          padding: '5px 14px',
          background: 'rgba(38,30,28,0.42)',
          borderRadius: 999,
          display: 'inline-block',
        }}>
          {isPreview ? 'PREVIEW · BEFORE SHARING' : 'CLASS WRAP-UP'}
        </div>
        <div style={{
          fontFamily: FONT_DISPLAY,
          fontSize: 38, fontWeight: 800, color: C.white,
          letterSpacing: 0.3, lineHeight: 1.1,
          textShadow: '0 3px 0 rgba(0,0,0,0.22)',
          marginTop: 6,
        }}>
          {role === 'coach' ? "Tonight's Highlights" : 'Great class'}
        </div>
        <div style={{
          fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.94)',
          marginTop: 10,
        }}>
          {role === 'coach' ? 'Review before sharing with the class' : "Here's who shined today"}
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 16, maxWidth: 820, width: '100%',
        position: 'relative',
        zIndex: 2,
      }}>
        {cards.map((a) => {
          const isYou = currentUserName ? a.student === currentUserName : false;
          return (
            <div key={a.key} style={{
              padding: 18,
              background: isYou
                ? `linear-gradient(135deg, ${a.color} 0%, ${a.color}DD 100%)`
                : C.white,
              borderRadius: 24,
              border: `3px solid ${isYou ? C.white : a.color}`,
              boxShadow: isYou
                ? `${clayShadow(a.color, 'lg')}, 0 0 24px ${a.color}66`
                : clayShadow(a.color, 'md'),
              position: 'relative',
            }}>
              {isYou && (
                <div style={{
                  position: 'absolute', top: -12, right: 14,
                  padding: '4px 12px',
                  background: C.white,
                  borderRadius: 999,
                  fontFamily: FONT_DISPLAY,
                  fontSize: 10, fontWeight: 800, color: a.color,
                  letterSpacing: 1.2,
                  boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                }}>YOU</div>
              )}
              <div style={{display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8}}>
                {isBrandIcon(a.icon) ? (
                  <div style={{
                    width: 56, height: 56,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    filter: isYou
                      ? 'drop-shadow(0 0 10px rgba(255,255,255,0.5)) drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
                      : `drop-shadow(0 0 8px ${a.color}55) drop-shadow(0 4px 8px rgba(0,0,0,0.18))`,
                  }}><RecIcon name={a.icon} size={52} color={isYou ? C.white : a.color} /></div>
                ) : (
                  <div style={{
                    width: 48, height: 48, borderRadius: 24,
                    background: isYou
                      ? 'rgba(255,255,255,0.28)'
                      : `linear-gradient(135deg, ${a.color} 0%, ${a.color}DD 100%)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: isYou
                      ? 'inset 0 2px 4px rgba(255,255,255,0.4)'
                      : bubbleShadow(a.color),
                  }}><RecIcon name={a.icon} size={24} color={C.white} /></div>
                )}
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={{
                    fontFamily: FONT_DISPLAY,
                    fontSize: 11, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase',
                    color: isYou ? 'rgba(255,255,255,0.95)' : a.color,
                  }}>{a.title}</div>
                </div>
              </div>
              <div style={{
                fontFamily: FONT_DISPLAY,
                fontSize: 19, fontWeight: 800,
                color: isYou ? C.white : C.brown,
                lineHeight: 1.15,
                textShadow: isYou ? '0 1px 0 rgba(0,0,0,0.15)' : 'none',
              }}>{a.student}</div>
              <div style={{
                fontSize: 12, fontWeight: 600, marginTop: 4,
                color: isYou ? 'rgba(255,255,255,0.9)' : C.textMuted,
              }}>{a.subtitle}</div>
            </div>
          );
        })}
      </div>

      <div style={{display: 'flex', gap: 10, marginTop: 28, position: 'relative', zIndex: 2}}>
        <button onClick={onDone} style={{
          padding: '14px 32px', borderRadius: 18,
          background: C.white,
          border: `3px solid ${C.white}`,
          color: C.orange,
          fontFamily: FONT_DISPLAY,
          fontSize: 16, fontWeight: 800, cursor: 'pointer',
          boxShadow: clayShadow(C.orange, 'lg'),
        }}>Awesome 🎉</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. StarFlyHtml — fires when stars increase.
//    Big gold star births at viewport center, holds, then arcs to the strip's
//    star count (#cc-star-target), shrinking 80px → 16px. Multi-star awards
//    emit N stars staggered 100ms apart. Strip pulses on landing.
//
//    Independent of OverlayManager — fires concurrently with other overlays.
// ─────────────────────────────────────────────────────────────────────────────
interface StarFlyHtmlProps {count: number; onDone: () => void;}

export function StarFlyHtml({count, onDone}: StarFlyHtmlProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof window === 'undefined') { onDone(); return; }

    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;
    const target = document.getElementById('cc-star-target');
    const targetRect = target?.getBoundingClientRect();
    const tx = targetRect ? targetRect.left + targetRect.width / 2 : cx;
    const ty = targetRect ? targetRect.top + targetRect.height / 2 : 60;

    const N = Math.max(1, Math.min(count, 5));
    let finished = 0;
    const cleanup: Array<() => void> = [];

    for (let i = 0; i < N; i++) {
      const star = document.createElement('div');
      star.style.cssText = `
        position: absolute;
        left: 0; top: 0;
        width: 140px; height: 140px;
        display: flex; align-items: center; justify-content: center;
        filter: drop-shadow(0 0 22px ${C.gold}AA) drop-shadow(0 6px 14px rgba(0,0,0,0.22));
        will-change: transform, opacity;
        pointer-events: none;
        opacity: 0;
        transform: scale(0);
      `;
      // Brand Star.svg (raster-in-SVG) served as static URL.
      star.innerHTML = `<img src="${STAR_IMG_URL}" width="100%" height="100%" alt="" style="display:block; user-select:none; pointer-events:none;" draggable="false" />`;
      container.appendChild(star);

      const delay = i * 110;
      const jitterX = (Math.random() - 0.5) * 50;
      const jitterY = (Math.random() - 0.5) * 30;
      // Container is 140px so anchor offset = 70 (half) to center on (cx, cy).
      const startX = cx - 70 + jitterX;
      const startY = cy - 70 + jitterY;
      const midX = (cx + tx) / 2 + (Math.random() - 0.5) * 60;
      const midY = Math.min(cy, ty) - 60 + (Math.random() - 0.5) * 30;
      const endX = tx - 14;
      const endY = ty - 14;

      // Phases (durations roughly):
      //   0%   star at center, scale 0
      //   12%  scale 1.45 (big overshoot)
      //   22%  scale 1.30 (settle big)
      //   42%  STILL at center, scale 0.55 (shrink in place — Disney "anticipation")
      //   75%  midpoint of arc, scale 0.35
      //   100% landed at strip pill, scale 0.15
      const anim = star.animate(
        [
          {transform: `translate3d(${startX}px, ${startY}px, 0) scale(0)`,    opacity: 0,    offset: 0},
          {transform: `translate3d(${startX}px, ${startY}px, 0) scale(1.45)`, opacity: 1,    offset: 0.12},
          {transform: `translate3d(${startX}px, ${startY}px, 0) scale(1.30)`, opacity: 1,    offset: 0.22},
          {transform: `translate3d(${startX}px, ${startY}px, 0) scale(0.55)`, opacity: 1,    offset: 0.42},
          {transform: `translate3d(${midX}px, ${midY}px, 0) scale(0.35)`,     opacity: 1,    offset: 0.75},
          {transform: `translate3d(${endX}px, ${endY}px, 0) scale(0.15)`,     opacity: 0.95, offset: 1},
        ],
        {duration: 1600, delay, easing: 'cubic-bezier(.34, .7, .42, 1)', fill: 'both'},
      );

      anim.onfinish = () => {
        // Landing pulse on the strip target
        if (target && target.animate) {
          target.animate(
            [
              {transform: 'scale(1)'},
              {transform: 'scale(1.16)'},
              {transform: 'scale(1)'},
            ],
            {duration: 320, easing: 'cubic-bezier(.34,1.56,.64,1)'},
          );
        }
        star.remove();
        finished++;
        if (finished >= N) onDone();
      };

      cleanup.push(() => {
        anim.cancel();
        if (star.parentNode) star.remove();
      });
    }

    return () => {
      cleanup.forEach(fn => fn());
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    />
  );
}
